import { useState, useCallback, useRef, useEffect } from 'react';
import api from '../api/api';

/**
 * useListContext – Centralized list-state management for filter, sort, pagination, and saved views.
 *
 * @param {string} moduleType - One of 'CONTACTS' | 'OPPORTUNITIES' | 'TASKS' | 'SUCCESS_PROFILES'
 * @param {object} defaultFilters - e.g. { stageFilter: 'ALL', verificationFilter: 'ALL' }
 * @param {object} defaultSort - e.g. { field: 'createdAt', order: 'desc' }
 */
export default function useListContext({ moduleType, defaultFilters = {}, defaultSort = { field: 'createdAt', order: 'desc' } }) {
    const [filters, setFilters] = useState({ ...defaultFilters });
    const [sort, setSort] = useState({ ...defaultSort });
    const [page, setPage] = useState(1);

    // Saved Views
    const [savedViews, setSavedViews] = useState([]);
    const [activeViewId, setActiveViewId] = useState(null);
    const [viewsLoading, setViewsLoading] = useState(false);

    // Reset page to 1 whenever filters or sort change
    const prevFilterRef = useRef(JSON.stringify(filters));
    const prevSortRef = useRef(JSON.stringify(sort));

    useEffect(() => {
        const filterStr = JSON.stringify(filters);
        const sortStr = JSON.stringify(sort);
        if (filterStr !== prevFilterRef.current || sortStr !== prevSortRef.current) {
            setPage(1);
            prevFilterRef.current = filterStr;
            prevSortRef.current = sortStr;
        }
    }, [filters, sort]);

    // Load saved views for this module
    const loadSavedViews = useCallback(async () => {
        setViewsLoading(true);
        try {
            const res = await api.get(`/api/saved-views?moduleType=${moduleType}`);
            setSavedViews(res.data);
        } catch (_) { /* silently degrade for non-blocking UX */ }
        finally { setViewsLoading(false); }
    }, [moduleType]);

    useEffect(() => { loadSavedViews(); }, [loadSavedViews]);

    // Apply a saved view
    const applyView = useCallback((view) => {
        if (!view) return;
        const filterState = view.filterState instanceof Map ? Object.fromEntries(view.filterState) : (view.filterState || {});
        const sortState = view.sortState instanceof Map ? Object.fromEntries(view.sortState) : (view.sortState || defaultSort);
        setFilters({ ...defaultFilters, ...filterState });
        setSort(sortState);
        setPage(1);
        setActiveViewId(view._id);
    }, [defaultFilters, defaultSort]);

    // Save current state as a new view
    const saveCurrentView = useCallback(async (name) => {
        if (!name || !name.trim()) return;
        try {
            const res = await api.post(`/api/saved-views`, {
                moduleType,
                name: name.trim(),
                filterState: filters,
                sortState: sort
            });
            setSavedViews(prev => [...prev, res.data]);
            setActiveViewId(res.data._id);
            return res.data;
        } catch (err) {
            throw err;
        }
    }, [moduleType, filters, sort]);

    // Update existing view
    const updateView = useCallback(async (viewId, updates) => {
        try {
            const res = await api.put(`/api/saved-views/${viewId}`, updates);
            setSavedViews(prev => prev.map(v => v._id === viewId ? res.data : v));
            return res.data;
        } catch (err) {
            throw err;
        }
    }, []);

    // Delete view
    const deleteView = useCallback(async (viewId) => {
        try {
            await api.delete(`/api/saved-views/${viewId}`);
            setSavedViews(prev => prev.filter(v => v._id !== viewId));
            if (activeViewId === viewId) setActiveViewId(null);
        } catch (err) {
            throw err;
        }
    }, [activeViewId]);

    // Clear filters to defaults
    const clearFilters = useCallback(() => {
        setFilters({ ...defaultFilters });
        setSort({ ...defaultSort });
        setPage(1);
        setActiveViewId(null);
    }, [defaultFilters, defaultSort]);

    // Convenience: update a single filter key
    const setFilter = useCallback((key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setActiveViewId(null); // Dirty state – no longer matches a saved view
    }, []);

    // Handle sort toggle
    const toggleSort = useCallback((field) => {
        setSort(prev => {
            if (prev.field === field) {
                return { field, order: prev.order === 'asc' ? 'desc' : 'asc' };
            }
            return { field, order: 'desc' };
        });
        setActiveViewId(null);
    }, []);

    return {
        // State
        filters,
        sort,
        page,
        activeViewId,
        savedViews,
        viewsLoading,

        // Actions
        setFilters,
        setFilter,
        setSort,
        toggleSort,
        setPage,
        clearFilters,

        // View Management
        applyView,
        saveCurrentView,
        updateView,
        deleteView,
        loadSavedViews
    };
}
